import {HttpClient, HttpHeaders, HttpParams} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {Observable} from 'rxjs';
import {NewConversation} from './chat-http-interfaces';
import {GeneralHttpHelperService} from '../../helpers/general-http-helper.service';
import {environment} from "../../../../environments/environment";

const CHAT_API_ROUTES = {
  CREATE_CONVERSATION: 'chat/conversations',
  GET_CONVERSATIONS: 'chat/conversations',
  POST_SHARE_ENTITY_TO_CONVERSATION: 'chat/conversations/share',
}

const BASE_V2_URL = `${environment.API_ENDPOINT_V2}`;

@Injectable({
  providedIn: 'root'
})
export class ChatHttpService {

  constructor(
    private httpClient: HttpClient,
    private generalHelperService: GeneralHttpHelperService
  ) {
  }

  private createConversationV2Req(chatData: NewConversation): Observable<any> {
    return this.httpClient.post(`${BASE_V2_URL}chats/conversations`, chatData);
  }

  createConversationV2(chatData: NewConversation): Promise<any> {
    return new Promise((resolve, reject) => {
      this.createConversationV2Req(chatData).subscribe({
        next: (res: any) => resolve(res),
        error: (error) => reject(error),
      });
    });
  }

  getConversationsV2Req(
    pageNumber: number,
    search?: string,
    perPage?: number
  ): Observable<any> {
    const params = [];

    if (search) {
      params.push(`search=${encodeURIComponent(search)}`);
    }
    if (perPage) {
      params.push(`per_page=${perPage}`);
    }
    if (pageNumber) {
      params.push(`page=${pageNumber}`);
    }

    const queryString = params.length > 0 ? `?${params.join('&')}` : '';
    return this.httpClient.get<any[]>(`${BASE_V2_URL}chats/conversations${queryString}`);
  }

  getConversationsV2(
    pageNumber: number,
    search?: string,
    perPage?: number
  ): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      this.getConversationsV2Req(pageNumber, search, perPage).subscribe({
        next: (res: any) => {
          resolve({
            data: res.data,
            pagination: this.generalHelperService.returnPagination(res),
          });
        },
        error: (error: any) => {
          reject(error);
        },
      });
    });
  }

  getSingleConversationV2Req(
    conversationId: number,
    pageNumber: number,
    search?: string,
    perPage?: number
  ): Observable<any> {
    const params = [];

    if (search) {
      params.push(`search=${encodeURIComponent(search)}`);
    }
    if (perPage) {
      params.push(`per_page=${perPage}`);
    }
    if (pageNumber) {
      params.push(`page=${pageNumber}`);
    }

    const queryString = params.length > 0 ? `?${params.join('&')}` : '';
    return this.httpClient.get<any[]>(`${BASE_V2_URL}chats/conversations/${conversationId}${queryString}`);
  }

  getSingleConversationV2(
    conversationId: number,
    pageNumber: number,
    search?: string,
    perPage?: number
  ): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      this.getSingleConversationV2Req(conversationId, pageNumber, search, perPage).subscribe({
        next: (res: any) => {
          resolve({
            conversation: res.conversation,
            data: res.messages.data,
            pagination: this.generalHelperService.returnPagination(res.messages)
          });
        },
        error: (error: any) => {
          reject(error);
        },
      });
    });
  }

  newMessageV2(conversationId: number, chatData: any, socketID?: any): Observable<any> {
    let headers = new HttpHeaders().set('X-Socket-Id', socketID ?? '');
    return this.httpClient.post<any>(`${BASE_V2_URL}chats/conversations/${conversationId}/messages`, chatData, {
      headers: headers
    });
  }

  archiveConversationV2(conversationId: number): Observable<any> {
    return this.httpClient.post<void>(`${BASE_V2_URL}chats/conversations/${conversationId}/archive`, null);
  }

  reactOnMessageV2(messageId: number, reqObj: any, socketID?: any): Observable<void> {
    let headers = new HttpHeaders().set('X-Socket-Id', socketID ?? '');
    return this.httpClient.post<void>(`${BASE_V2_URL}chats/conversations/message-reactions/${messageId}`, reqObj, {
      headers: headers
    });
  }

  getChatMediaV2Req(conversationId: number, pageNumber: number, perPage?: number): Observable<any> {
    const params = [];

    if (perPage) {
      params.push(`per_page=${perPage}`);
    }
    if (pageNumber) {
      params.push(`page=${pageNumber}`);
    }

    const queryString = params.length > 0 ? `?${params.join('&')}` : '';
    return this.httpClient.get(`${BASE_V2_URL}chats/conversations/${conversationId}/get-media${queryString}`);
  }

  getChatMediaV2(conversationId: number, pageNumber: number, perPage?: number): Promise<any> {
    return new Promise((resolve, reject) => {
      this.getChatMediaV2Req(conversationId, pageNumber, perPage).subscribe({
        next: (res: any) => {
          resolve({
            data: res.data,
            pagination: this.generalHelperService.returnPagination(res)
          })
        },
        error: (error: any) => {
          reject(error);
        }
      });
    });
  }

  getChatPDFsV2Req(conversationId: number, pageNumber: number, perPage?: number): Observable<any> {
    const params = [];

    if (perPage) {
      params.push(`per_page=${perPage}`);
    }
    if (pageNumber) {
      params.push(`page=${pageNumber}`);
    }

    const queryString = params.length > 0 ? `?${params.join('&')}` : '';
    return this.httpClient.get(`${BASE_V2_URL}chats/conversations/${conversationId}/get-pdfs${queryString}`);
  }

  getChatPDFsV2(conversationId: number, pageNumber: number, perPage?: number): Promise<any> {
    return new Promise((resolve, reject) => {
      this.getChatPDFsV2Req(conversationId, pageNumber, perPage).subscribe({
        next: (res: any) => {
          resolve({
            data: res.data,
            pagination: this.generalHelperService.returnPagination(res)
          })
        },
        error: (error: any) => {
          reject(error);
        }
      });
    });
  }

  getUsersForChatV2Req(pageNumber: number,
                       conversationId?: number,
                       search?: string,
                       perPage?: number): Observable<any> | any {
    const params = [];

    if (search) {
      params.push(`search=${encodeURIComponent(search)}`);
    }
    if (perPage) {
      params.push(`per_page=${perPage}`);
    }
    if (pageNumber) {
      params.push(`page=${pageNumber}`);
    }

    let baseUrl = conversationId ? `users/get-users-for-chat/${conversationId}` : 'users/get-users-for-chat';

    const queryString = params.length > 0 ? `?${params.join('&')}` : '';
    return this.httpClient.get<any[]>(`${BASE_V2_URL}${baseUrl}${queryString}`);
  }

  getUsersForChatV2(pageNumber: number,
                    conversationId?: number,
                    search?: string,
                    perPage?: number): Promise<any> {
    return new Promise((resolve, reject) => {
      this.getUsersForChatV2Req(pageNumber, conversationId, search, perPage).subscribe({
        next: (res: any) => {
          resolve({
            data: res.data ?? res,
            pagination: this.generalHelperService.returnPagination(res)
          });
        },
        error: (error: any) => {
          reject(error);
        }
      });
    });
  }

  updateMessageV2(messageId: number, chatData: any, socketID?: any): Observable<any> {
    let headers = new HttpHeaders().set('X-Socket-Id', socketID ?? '');
    return this.httpClient.put<any>(`${BASE_V2_URL}chats/conversations/messages/${messageId}`, chatData, {
      headers: headers
    });
  }

  deleteMessageV2(messageId: number, socketID?: any): Observable<any> {
    let headers = new HttpHeaders().set('X-Socket-Id', socketID ?? '');
    return this.httpClient.delete<any>(`${BASE_V2_URL}chats/conversations/messages/${messageId}`, {
      headers: headers
    });
  }

  downloadMediaV2(mediaId: number): Observable<Blob> {
    return this.httpClient.get<Blob>(`${BASE_V2_URL}chats/conversations/download-media/${mediaId}`, {
      responseType: 'blob' as 'json',
    });
  }

  addParticipantsToGroupConversationV2(conversationId: number, reqBody: any) {
    return this.httpClient.post<any>(`${BASE_V2_URL}chats/conversations/${conversationId}/add-participants`, reqBody);
  }

  leaveConversationV2(conversationId: number): Observable<any> {
    return this.httpClient.delete<void>(`${BASE_V2_URL}chats/conversations/${conversationId}/leave-conversation`);
  }

  updateGroupChatNameAndPhotoV2(conversationId: number, formData: FormData) {
    return this.httpClient.post<any>(`${BASE_V2_URL}chats/conversations/${conversationId}`, formData);
  }

  removeParticipantFromGroupV2(conversationId: number, participantId: number, socketID?: any) {
    let headers = new HttpHeaders().set('X-Socket-Id', socketID ?? '');
    return this.httpClient.delete<any>(`${BASE_V2_URL}chats/conversations/${conversationId}/${participantId}/remove-participant`, {headers: headers});
  }

  postShareToConversationsV2Req(bodyParams: any): Observable<any> {
    return this.httpClient.post(`${BASE_V2_URL}chats/conversations/share`, bodyParams);
  }

  postShareToConversationsV2(bodyParams: any): Promise<any> {
    return new Promise((resolve, reject) => {
      this.postShareToConversationsV2Req(bodyParams).subscribe({
        next: (res: any) => resolve(res),
        error: (error) => reject(error),
      });
    });
  }
}
